import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast, ToastContainer } from './Toast';
import axiosClient from '../services/axiosClient';
import './FavoriteOffers.css';
import './ModernFavoriteCard.css';

// Fonction utilitaire pour lire le token du localStorage
function getAuthHeader() {
  try {
    // Essayer d'abord la structure principale du localStorage
    const authData = localStorage.getItem('authData');
    if (authData) {
      const data = JSON.parse(authData);
      if (data?.token && data?.tokenType) {
        return { Authorization: `${data.tokenType} ${data.token}` };
      }
    }

    // Fallback sur d'autres clés possibles
    const storages = [window.localStorage, window.sessionStorage];
    const keys = ['auth','userData','senoffre_auth','loginData','currentUser','user','userDetails'];
    for (const storage of storages) {
      for (const key of keys) {
        const raw = storage.getItem(key);
        if (!raw) continue;
        const data = JSON.parse(raw);
        if (data?.token) return { Authorization: `${data.tokenType || 'Bearer'} ${data.token}` };
      }
    }
    return {};
  } catch (error) {
    console.error('Erreur lors de la récupération du token:', error);
    return {};
  }
}

// Composant pour une offre favorite individuelle
function FavoriteOfferCard({ offer, onView, onRemove, getCityName }) {
  const [removing, setRemoving] = useState(false);

  const handleRemove = async () => {
    setRemoving(true);
    await onRemove(offer);
    setRemoving(false);
  };

  // Extraire les données de l'offre (peut être dans offer.post ou directement dans offer)
  const jobData = offer.post || offer;
  const company = jobData.company_name || jobData.org || 'Entreprise';
  const title = jobData.title || 'Poste non spécifié';
  
  // Utiliser getCityName pour résoudre le city_id
  const location = getCityName ? getCityName(jobData.city_id) : (jobData.city || jobData.location || 'Localisation non spécifiée');
  
  const salary = jobData.salary_formatted || jobData.salary || '';
  const type = jobData.post_type || jobData.type || 'CDI';
  const createdAt = offer.created_at || jobData.created_at || '';

  console.log('JobData pour localisation:', { 
    city_id: jobData.city_id, 
    city: jobData.city, 
    location: jobData.location,
    resolved_location: location 
  });

  // Formater la date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays === 1) return 'Ajouté hier';
      if (diffDays < 7) return `Ajouté il y a ${diffDays} jours`;
      if (diffDays < 30) return `Ajouté il y a ${Math.ceil(diffDays / 7)} semaines`;
      return `Ajouté il y a ${Math.ceil(diffDays / 30)} mois`;
    } catch (e) {
      return 'Récemment ajouté';
    }
  };

  return (
    <div className="modern-favorite-card">
      {/* Barre de couleur en haut */}
      <div className="card-top-bar"></div>
      
      {/* Header avec logo */}
      <div className="card-header">
        <div className="company-logo">
          {jobData.logoUrl ? (
            <img src={jobData.logoUrl} alt={company} onError={(e) => e.target.style.display = 'none'} />
          ) : (
            <div className="logo-fallback">
              {company.charAt(0).toUpperCase()}
            </div>
          )}
        </div>
        
        <button 
          className={`favorite-heart-btn ${removing ? 'removing' : ''}`}
          onClick={handleRemove}
          disabled={removing}
          title="Retirer des favoris"
        >
          <i className={`fa ${removing ? 'fa-spinner fa-spin' : 'fa-heart'}`}></i>
        </button>
      </div>

      {/* Contenu principal */}
      <div className="card-body">
        <h3 className="job-title">{title}</h3>
        <p className="company-name">{company}</p>
        
        {/* Informations détaillées */}
        <div className="job-details">
          <div className="detail-row">
            <div className="detail-item">
              <i className="fa fa-map-marker-alt"></i>
              <span>{location}</span>
            </div>
            <div className="detail-item">
              <i className="fa fa-briefcase"></i>
              <span>{type}</span>
            </div>
          </div>
          
          {salary && (
            <div className="salary-badge">
              <i className="fa fa-money-bill-wave"></i>
              <span>{salary}</span>
            </div>
          )}
        </div>
        
        {/* Date d'ajout */}
        <div className="added-date">
          <i className="fa fa-clock"></i>
          <span>{formatDate(createdAt)}</span>
        </div>
      </div>

      {/* Actions */}
      <div className="card-footer">
        <button 
          className="view-details-btn"
          onClick={() => onView(offer)}
        >
          <span>Voir les détails</span>
          <i className="fa fa-arrow-right"></i>
        </button>
      </div>
    </div>
  );
}

// Composant principal pour les offres favorites
export default function FavoriteOffers({ 
  apiEndpoint = '',
  detailRouteBase = '/job-detail',
  maxItems = 10 
}) {
  const navigate = useNavigate();
  const { toasts, showToast, removeToast } = useToast();
  
  const [favoriteOffers, setFavoriteOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);

  // Fonction pour récupérer les villes du Sénégal
  const fetchCities = async () => {
    setLoadingCities(true);
    try {
      const response = await axiosClient.get('/countries/SN/cities?perPage=100', {
        headers: {
          'Accept': 'application/json',
          ...getAuthHeader(),
        },
      });
      
      if (response.status >= 200 && response.status < 300) {
        const data = response.data;
        let cityList = data?.result?.data || data?.data || data?.result || data || [];
        if (!Array.isArray(cityList)) {
          if (cityList && Array.isArray(cityList.data)) cityList = cityList.data;
          else cityList = [];
        }
        
        // Normaliser les données des villes selon la structure API réelle
        const normalizedCities = cityList.map(city => ({
          id: city.id,
          name: city.name,
          country_code: city.country_code,
          latitude: city.latitude,
          longitude: city.longitude,
          population: city.population,
          posts_count: city.posts_count || 0
        }));
        
        setCities(normalizedCities);
        console.log('Villes récupérées:', normalizedCities);
      }
    } catch (e) {
      console.error('[Cities API] fetch error:', e);
      // Fallback avec villes par défaut selon la structure API
      setCities([
        { id: 1, name: 'Dakar', country_code: 'SN', latitude: 14.69, longitude: -17.44, population: 1030594, posts_count: 0 },
        { id: 2, name: 'Thiès', country_code: 'SN', latitude: 14.79, longitude: -16.93, population: 320000, posts_count: 0 },
        { id: 3, name: 'Saint-Louis', country_code: 'SN', latitude: 16.02, longitude: -16.48, population: 176000, posts_count: 0 },
        { id: 4, name: 'Kaolack', country_code: 'SN', latitude: 14.15, longitude: -16.07, population: 233708, posts_count: 0 },
        { id: 5, name: 'Ziguinchor', country_code: 'SN', latitude: 12.58, longitude: -16.27, population: 230000, posts_count: 0 },
        { id: 6, name: 'Tambacounda', country_code: 'SN', latitude: 13.77, longitude: -13.67, population: 78800, posts_count: 0 }
      ]);
    } finally {
      setLoadingCities(false);
    }
  };

  // Fonction pour récupérer les offres favorites
  const fetchFavoriteOffers = async () => {
    setLoading(true);
    setError('');
    
    try {
      const authHeaders = getAuthHeader();
      
      if (!authHeaders.Authorization) {
        setError('Vous devez être connecté pour voir vos favoris');
        setFavoriteOffers([]);
        return;
      }

      const response = await axiosClient.get('/savedPosts', {
        params: {
          country_code: 'SN',
          embed: 'post,user',
          sort: 'created_at',
          perPage: maxItems,
        },
        headers: {
          'Accept': 'application/json',
          ...authHeaders,
        },
      });
      
      if (response.status >= 200 && response.status < 300) {
        const data = response.data;
        let savedList = data?.result?.data || data?.data || data?.result || data || [];
        
        if (!Array.isArray(savedList)) {
          if (savedList && Array.isArray(savedList.data)) savedList = savedList.data;
          else savedList = [];
        }
        
        console.log('Offres favorites récupérées:', savedList);
        setFavoriteOffers(savedList);
      } else {
        throw new Error(`Erreur ${response.status}`);
      }
    } catch (e) {
      console.error('[FavoriteOffers] fetch error:', e);
      const msg = e?.message || 'Erreur réseau';
      setError(`Impossible de charger vos favoris: ${msg}`);
      setFavoriteOffers([]);
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour résoudre le nom de la ville à partir du city_id
  const getCityName = (cityId) => {
    if (!cityId || cities.length === 0) return 'Localisation non spécifiée';
    
    const city = cities.find(c => c.id == cityId); // Utiliser == pour gérer les types différents
    return city ? city.name : 'Localisation non spécifiée';
  };

  // Charger les données au montage du composant
  useEffect(() => {
    fetchCities();
  }, []);

  // Charger les favoris après avoir récupéré les villes
  useEffect(() => {
    if (cities.length > 0) {
      fetchFavoriteOffers();
    }
  }, [cities]);

  // Fonction pour voir une offre
  const handleViewOffer = (offer) => {
    const jobData = offer.post || offer;
    const id = jobData?.id || jobData?.slug || jobData?.reference;
    if (id) {
      navigate(`${detailRouteBase}?id=${encodeURIComponent(id)}`);
    }
  };

  // Fonction pour retirer une offre des favoris
  const handleRemoveOffer = async (offer) => {
    try {
      const authHeaders = getAuthHeader();
      const jobData = offer.post || offer;
      const postId = jobData?.id;
      
      if (!postId) {
        showToast('Erreur: ID de l\'offre introuvable', 'error');
        return;
      }

      const response = await axiosClient.delete(`/savedPosts/${postId}`, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...authHeaders,
        },
      });

      if (response.ok) {
        // Retirer l'offre de la liste locale
        setFavoriteOffers(prev => prev.filter(item => {
          const itemJobData = item.post || item;
          return itemJobData.id !== postId;
        }));
        
        showToast('Offre retirée de vos favoris', 'info');
      } else {
        throw new Error('Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      showToast('Erreur lors de la suppression de l\'offre', 'error');
    }
  };

  return (
    <div className="favorite-offers-container">
      {/* Header */}
      <div className="favorite-offers-header">
        <div className="header-left">
          <h2 className="section-title">
            <i className="fa fa-heart me-2"></i>
            Mes Offres Favorites
          </h2>
          <p className="section-subtitle">
            {loading ? 'Chargement...' : `${favoriteOffers.length} offre${favoriteOffers.length > 1 ? 's' : ''} sauvegardée${favoriteOffers.length > 1 ? 's' : ''}`}
          </p>
        </div>
        
        <div className="header-right">
          <button 
            className="refresh-btn"
            onClick={fetchFavoriteOffers}
            disabled={loading}
          >
            <i className={`fa fa-refresh ${loading ? 'fa-spin' : ''}`}></i>
            Actualiser
          </button>
          
          <button 
            className="browse-btn"
            onClick={() => navigate('/consultant/dashboard')}
          >
            <i className="fa fa-search me-2"></i>
            Parcourir Les Offres
          </button>
        </div>
      </div>

      {/* Contenu */}
      <div className="favorite-offers-content">
        {loading && (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Chargement de vos favoris...</p>
          </div>
        )}
        
        {error && !loading && (
          <div className="error-state">
            <i className="fa fa-exclamation-triangle"></i>
            <h3>Erreur</h3>
            <p>{error}</p>
            <button onClick={fetchFavoriteOffers}>Réessayer</button>
          </div>
        )}
        
        {!loading && !error && favoriteOffers.length === 0 && (
          <div className="empty-state">
            <i className="fa fa-heart-o"></i>
            <h3>Aucune offre favorite</h3>
            <p>Vous n'avez pas encore sauvegardé d'Appels d'offres</p>
            <button 
              className="browse-offers-btn"
              onClick={() => navigate('/consultant/dashboard')}
            >
              <i className="fa fa-search me-2"></i>
              Parcourir les offres
            </button>
          </div>
        )}
        
        {!loading && !error && favoriteOffers.length > 0 && (
          <div className="favorite-offers-grid">
            {favoriteOffers.map((offer, index) => (
              <FavoriteOfferCard 
                key={offer.id || index}
                offer={offer}
                onView={handleViewOffer}
                onRemove={handleRemoveOffer}
                getCityName={getCityName}
              />
            ))}
          </div>
        )}
      </div>
      
      {/* Container pour les toasts */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
}
